import { Progress } from '@/components/shadcn/progress'
import { cn } from '@/lib/utils'
import { ProgressCardConfig, CardData } from '../types'

interface ProgressCardProps {
  config: ProgressCardConfig
  data: CardData
}

type StatusTier = 'low' | 'mid' | 'high'

const DEFAULT_COLOR_STATUS_THRESHOLDS: [number, number] = [15, 50]
const CIRCLE_SIZE = 80
const CIRCLE_STROKE_WIDTH = 8

function getStatusTier(progress: number, thresholds: [number, number]): StatusTier {
  const [lowMid, midHigh] = thresholds

  if (progress < lowMid) return 'low'
  if (progress < midHigh) return 'mid'
  return 'high'
}

function getStatusColorClasses(
  progress: number,
  barStatusColor: ProgressCardConfig['barStatusColor'],
  colorStatusThresholds?: ProgressCardConfig['colorStatusThresholds']
): { bar?: string; circle?: string } {
  if (!barStatusColor) return {}

  const thresholds = colorStatusThresholds ?? DEFAULT_COLOR_STATUS_THRESHOLDS
  const tier = getStatusTier(progress, thresholds)
  const colors: Record<
    StatusTier,
    { normal: { bar: string; circle: string }; reverse: { bar: string; circle: string } }
  > = {
    low: {
      normal: { bar: 'bg-statusred-500', circle: 'stroke-statusred-500' },
      reverse: { bar: 'bg-statusgreen-500', circle: 'stroke-statusgreen-500' },
    },
    mid: {
      normal: { bar: 'bg-statusyellow-500', circle: 'stroke-statusyellow-500' },
      reverse: { bar: 'bg-statusyellow-500', circle: 'stroke-statusyellow-500' },
    },
    high: {
      normal: { bar: 'bg-statusgreen-500', circle: 'stroke-statusgreen-500' },
      reverse: { bar: 'bg-statusred-500', circle: 'stroke-statusred-500' },
    },
  }

  return colors[tier][barStatusColor]
}

function BarProgress({
  progress,
  label,
  indicatorClassName,
}: {
  progress: number
  label?: string
  indicatorClassName?: string
}) {
  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center gap-3">
        <Progress
          className="flex-1"
          value={progress}
          aria-label={label ?? `Progress: ${progress}%`}
          indicatorClassName={indicatorClassName}
        />
        <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{progress}%</span>
      </div>
      {label && <div className="text-sm text-muted-foreground">{label}</div>}
    </div>
  )
}

function CircleProgress({
  progress,
  label,
  strokeClassName,
}: {
  progress: number
  label?: string
  strokeClassName?: string
}) {
  const radius = (CIRCLE_SIZE - CIRCLE_STROKE_WIDTH) / 2
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference - (progress / 100) * circumference

  return (
    <div className="flex w-full items-center gap-4">
      <div
        className="relative shrink-0"
        style={{ width: CIRCLE_SIZE, height: CIRCLE_SIZE }}
        role="img"
        aria-label={label ?? `Progress: ${progress}%`}
      >
        <svg
          width={CIRCLE_SIZE}
          height={CIRCLE_SIZE}
          viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}
          className="-rotate-90"
        >
          <circle
            cx={CIRCLE_SIZE / 2}
            cy={CIRCLE_SIZE / 2}
            r={radius}
            fill="none"
            strokeWidth={CIRCLE_STROKE_WIDTH}
            className="stroke-foreground/10"
          />
          <circle
            cx={CIRCLE_SIZE / 2}
            cy={CIRCLE_SIZE / 2}
            r={radius}
            fill="none"
            strokeWidth={CIRCLE_STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className={cn('transition-[stroke-dashoffset] duration-300', strokeClassName ?? 'stroke-primary')}
          />
        </svg>
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-semibold tabular-nums text-foreground">
          {progress}%
        </span>
      </div>
      {label && <div className="min-w-0 flex-1 text-sm text-muted-foreground">{label}</div>}
    </div>
  )
}

export function ProgressCard({ config, data }: ProgressCardProps) {
  const progress = Math.min(100, Math.max(0, data.progress ?? 0))
  const colorClasses = getStatusColorClasses(progress, config.barStatusColor, config.colorStatusThresholds)

  if (config.shape === 'circle') {
    return <CircleProgress progress={progress} label={data.label} strokeClassName={colorClasses.circle} />
  }

  return <BarProgress progress={progress} label={data.label} indicatorClassName={colorClasses.bar} />
}
